import * as React from 'react'

import debounce from 'lodash.debounce'

import CloudDoneIcon from '@mui/icons-material/CloudDone'
import { Chip } from '@mui/material'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import Pagination from '@mui/material/Pagination'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import TextField from '@podcastsoundboard/ps-lib/components/AccessibleTextField'

import searchVoicySounds from '../../api/voicySounds/search'
import ClosableDialogTitle from '../../components/ClosableDialogTitle'
import { VoicyClip } from '../../types/Voicy'
import AudioPlayer from '../AudioPlayer'

const PER_PAGE = 50

function VoicyRow({
  voicySound,
  onSelected,
}: {
  voicySound: VoicyClip
  onSelected: (id: string) => void
}) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ pr: 1 }}>
      <Stack
        style={{
          height: '5rem',
          overflow: 'hidden',
          borderRadius: 5,
          aspectRatio: '1/1',
        }}
        direction={'column'}
        alignItems={'center'}
      >
        <img
          src={voicySound.thumbnail}
          alt={voicySound.name}
          style={{ height: '100%', borderRadius: 5, backgroundColor: '#ccc' }}
        />
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        flexGrow="1"
        sx={{ pt: 0.5, ml: -0.5, pb: 0.5 }}
        flexWrap="wrap"
      >
        <Box flexGrow="1">
          <Typography
            style={{
              overflow: 'hidden',
              display: 'block',
              textOverflow: 'ellipsis',
            }}
            variant="body1"
            sx={{ pl: 1 }}
          >
            {voicySound.name}
          </Typography>

          <AudioPlayer audioUrl={voicySound.sound} />
        </Box>

        <Chip
          clickable
          label="Import"
          onClick={() => onSelected(voicySound.id)}
          size="small"
          color="primary"
          icon={<CloudDoneIcon />}
        />
      </Stack>
    </Stack>
  )
}

export default function VoicyBrowser({
  open,
  // onSoundfileSelected,
  onClose,
  handleSoundSelected,
  onAddSnackbar,
  darkModeEnabled,
  logoSrc,
  fullScreen,
}: {
  open: boolean
  onClose: () => void
  handleSoundSelected: (id: string) => void
  onAddSnackbar: (text: string) => void
  darkModeEnabled: boolean
  logoSrc?: string
  fullScreen?: boolean
}) {
  const [searchLoading, setSearchLoading] = React.useState(false)
  const [query, setQuery] = React.useState('')
  const [page, setPage] = React.useState(1)
  const [totalPages, setTotalPages] = React.useState(1)

  const [voicySounds, setVoicySounds] = React.useState<VoicyClip[]>([])

  const handleSearchVoicySounds = React.useCallback(() => {
    if (!query) return
    searchVoicySounds({
      query,
      page,
      perPage: PER_PAGE,
    })
      .then(({ voicySounds, totalPages }) => {
        if (totalPages < page) setPage(totalPages)
        setVoicySounds(voicySounds)
        setTotalPages(totalPages)
        setSearchLoading(false)
      })
      .catch((err) => {
        setSearchLoading(false)
        const text =
          (err.response && err.response.data && err.response.data.error) ||
          err.message

        onAddSnackbar(text)
      })
  }, [onAddSnackbar, page, query])

  React.useEffect(() => {
    setSearchLoading(true)

    const debounced = debounce(() => {
      handleSearchVoicySounds()
    }, 300)

    debounced()

    return debounced.cancel
  }, [query, handleSearchVoicySounds])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      className={fullScreen ? 'mobile-safe-padding-dialog' : ''}
      maxWidth={fullScreen ? false : 'sm'}
    >
      <ClosableDialogTitle onClose={onClose}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={2}
          sx={{ py: 1, px: 2 }}
        >
          <img
            role="presentation"
            src={logoSrc || '/img/voicy-logo.svg'}
            style={{
              height: '30px',
              marginLeft: '-20px',
            }}
          />

          <span className={darkModeEnabled ? 'dark-mode-links' : ''}>
            Import sounds from Voicy
          </span>
        </Stack>
      </ClosableDialogTitle>

      <DialogContent sx={{ mb: 2 }}>
        <TextField
          sx={{ mt: 1, mb: 2 }}
          size="small"
          value={query || ''}
          onChange={(e) => setQuery(e.target.value)}
          label="Search sounds"
          fullWidth
          variant="outlined"
        />

        {!query && (
          <Typography variant="body1" color="text.secondary">
            Please enter search query.
          </Typography>
        )}
        {query && (
          <>
            {searchLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <CircularProgress />
              </Box>
            )}

            {totalPages > 1 && (
              <Pagination
                sx={{ mb: 2 }}
                count={totalPages}
                page={page}
                onChange={(_, v) => setPage(v)}
              />
            )}
            {!searchLoading && voicySounds.length === 0 && (
              <Typography>No sounds found.</Typography>
            )}
            <Stack direction="column" spacing={2}>
              {voicySounds.length > 0 &&
                voicySounds &&
                voicySounds.map((voicySound) => (
                  <Paper key={voicySound.id}>
                    <VoicyRow
                      voicySound={voicySound}
                      onSelected={handleSoundSelected}
                    />
                  </Paper>
                ))}
            </Stack>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
